import React from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCreditCard,
  FiShoppingCart,
} from "react-icons/fi";

import {
  CartLayout,
  CartMobileBar,
  CartSection,
  CheckoutSection,
  SectionTitle,
  SubmitButton,
} from "../CartPage.styles";
import CartItemsList from "./CartItemsList";
import CartOrderSummary, { CartTrustRow } from "./OrderSummary";
import { formatPrice } from "../../../../utils/utils";

const linkBackStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  color: "var(--text-secondary)",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: "0.85rem",
  padding: "0.5rem 0",
  marginBottom: "1rem",
  transition: "color 0.2s ease",
};

const stepMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.25 },
};

export default function CartReviewStep({
  cart,
  itemsByShippingGroup,
  paginatedCentralItems,
  centralPage,
  centralTotalPages,
  paginatedBandGroups,
  totals,
  country,
  onUpdateQuantity,
  onRemoveItem,
  onCentralPageChange,
  onBandPageChange,
  onContinue,
  onBackToShop,
}) {
  const hasItems = cart.items?.length > 0;

  // Grupe pošiljki (ko šalje šta) su uvek vidljive, kupac mora da zna
  // da li porudžbina stiže u više paketa
  const showGroupHeaders = true;

  return (
    <motion.div {...stepMotion}>
      <button type="button" style={linkBackStyle} onClick={onBackToShop}>
        <FiArrowLeft size={14} />
        Nastavi kupovinu
      </button>

      <CartLayout>
        <div>
          <CartSection>
            <SectionTitle>
              <FiShoppingCart />
              Proizvodi ({cart.totalItems})
            </SectionTitle>

            {itemsByShippingGroup.centralShipping.length > 0 && (
              <CartItemsList
                items={paginatedCentralItems}
                totalPages={centralTotalPages}
                currentPage={centralPage}
                onPageChange={onCentralPageChange}
                groupTitle="Centralna dostava"
                showGroupHeader={showGroupHeaders}
                onUpdateQuantity={onUpdateQuantity}
                onRemoveItem={onRemoveItem}
              />
            )}

            {Array.from(paginatedBandGroups.entries()).map(
              ([sellerKey, bandGroup]) => (
                <CartItemsList
                  key={sellerKey}
                  items={bandGroup.items}
                  totalPages={bandGroup.totalPages}
                  currentPage={bandGroup.currentPage}
                  onPageChange={(page) => onBandPageChange(sellerKey, page)}
                  groupTitle={
                    bandGroup.sellerType === "festival"
                      ? `Dostava od festivala: ${bandGroup.sellerName}`
                      : `Dostava od benda: ${bandGroup.sellerName}`
                  }
                  showGroupHeader={showGroupHeaders}
                  onUpdateQuantity={onUpdateQuantity}
                  onRemoveItem={onRemoveItem}
                />
              ),
            )}
          </CartSection>
        </div>

        <div>
          <CheckoutSection>
            <SectionTitle>
              <FiCreditCard />
              Pregled korpe
            </SectionTitle>

            <CartOrderSummary
              totals={totals}
              country={country}
              hasItems={hasItems}
              showShipping={false}
            />

            <SubmitButton
              type="button"
              onClick={onContinue}
              disabled={!hasItems}
              style={{ marginTop: "1.25rem" }}
            >
              <FiArrowRight />
              Nastavi na plaćanje
            </SubmitButton>

            <CartTrustRow />
          </CheckoutSection>
        </div>
      </CartLayout>

      {/* Mobilna sticky traka (v2 s.9.5), kroz portal jer motion.div
          (transform) pravi containing block i lomi position: fixed */}
      {createPortal(
        <CartMobileBar>
          <div className="total">
            <strong>{formatPrice(totals.total)}</strong>
            <span>ukupno</span>
          </div>
          <SubmitButton type="button" onClick={onContinue} disabled={!hasItems}>
            Nastavi na plaćanje
          </SubmitButton>
        </CartMobileBar>,
        document.body,
      )}
    </motion.div>
  );
}
